/**
 * 运行轨迹抽屉 · ② 轨迹栏（PR5 新写；线框 01 §一 ②，判定卡 03 差异 3～5、9～11，ref 04 O-1）。
 * 固定顺序分组：信号 → 召回 → 计划 → 事项 × N → 生成；每组可折叠、收起态带摘要，默认只展开当前事项；
 * 组内每行「层名 · 一句摘要 · 状态」，触发的护栏带状态色放在它那一步；底部一行本轮耗时 ／ token ／ 花费按层拆。
 * 分组折叠块先在抽屉内实现（ref 02：第二处要用时再登记缺口）。
 */
import { ChevronDown, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { LoadingInline } from "@/components/ui/feedback";
import { cn } from "@/lib/utils";
import { FailState } from "../kit/fail-state";
import { PanelHead } from "./execution-drawer";
import {
	type CostLine,
	type TraceGroup,
	type TraceModel,
	type TraceRow,
	fmtMs,
} from "./trace-model";

export type TraceSel =
	| { kind: "group"; key: string }
	| { kind: "row"; id: string; groupKey: string }
	| { kind: "bg" }
	| null;

export function TraceColumn({
	turnNo,
	model,
	loading,
	error,
	onRetry,
	retrying,
	pending,
	expanded,
	onToggle,
	selected,
	onSelect,
}: {
	turnNo: number;
	model: TraceModel | null;
	loading?: boolean;
	error?: string | null;
	onRetry?: () => void;
	retrying?: boolean;
	/** 人工提问：本轮已发出、正在跑 */
	pending?: boolean;
	expanded: Set<string>;
	onToggle: (key: string) => void;
	selected: TraceSel;
	onSelect: (s: TraceSel) => void;
}) {
	let body: ReactNode;
	if (pending)
		body = (
			<div
				className="flex items-center gap-2 rounded-lg bg-surface-subtle px-3 py-2 text-xs text-text-secondary"
				data-testid="plan-trace-running"
			>
				<span className="inline-block h-2 w-2 animate-pulse rounded-full bg-brand-accent" />
				执行中（接续判定 → 事项 → 生成，跑完后逐层展开）
			</div>
		);
	else if (loading) body = <LoadingInline text="加载这一轮的轨迹…" />;
	else if (error)
		body = (
			<FailState
				testId="plan-trace-turn-fail"
				title="这一轮的轨迹没取到"
				reason={error}
				onRetry={onRetry}
				retrying={retrying}
			/>
		);
	else if (!model) body = <div className="text-xs text-text-muted">这一轮还没有轨迹。</div>;
	else if (!model.layered)
		body = (
			<div className="space-y-2" data-testid="plan-trace-flat">
				<div className="rounded-lg bg-surface-subtle px-3 py-2 text-xs text-text-secondary">
					ⓘ 此记录生成于分层留痕上线前，按节点平铺显示
				</div>
				{model.flat.length ? (
					model.flat.map((r, i) => (
						<StepRow
							key={r.id}
							row={r}
							seq={i + 1}
							selected={selected?.kind === "row" && selected.id === r.id}
							onSelect={() => onSelect({ kind: "row", id: r.id, groupKey: "flat" })}
						/>
					))
				) : (
					<div className="text-xs text-text-muted">本轮没有留痕事件。</div>
				)}
			</div>
		);
	else
		body = (
			<div className="space-y-2" data-testid="plan-trace-groups">
				{model.groups.map((g) => (
					<GroupBlock
						key={g.key}
						g={g}
						open={expanded.has(g.key)}
						onToggle={() => onToggle(g.key)}
						selected={selected}
						onSelect={onSelect}
					/>
				))}
				{!model.currentKey && model.groups.some((g) => g.kind === "item") ? (
					<div className="px-1 text-xs text-text-muted" data-testid="plan-trace-no-item">
						本轮没有推进任何事项（全部收起）。
					</div>
				) : null}
			</div>
		);
	return (
		<div className="flex h-full min-h-0 flex-col" data-testid="plan-col-steps">
			<div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
				<PanelHead title={`轨迹（第 ${turnNo} 轮）`} hint="点一行看详情" />
				{body}
			</div>
			<CostFooter cost={pending || loading || error ? null : (model?.cost ?? null)} />
		</div>
	);
}

function GroupBlock({
	g,
	open,
	onToggle,
	selected,
	onSelect,
}: {
	g: TraceGroup;
	open: boolean;
	onToggle: () => void;
	selected: TraceSel;
	onSelect: (s: TraceSel) => void;
}) {
	const isSel = selected?.kind === "group" && selected.key === g.key;
	const canOpen = g.available && g.rows.length > 0;
	const greyed = !g.available;
	const clickable = !(g.kind === "recall" && greyed);
	return (
		<div
			className={cn(
				"rounded-lg border",
				isSel ? "border-brand-accent bg-brand-accent/5" : "border-surface-border bg-surface-card",
			)}
			data-testid="plan-trace-group"
			data-key={g.key}
			data-expanded={open && canOpen ? "1" : "0"}
		>
			<div className="flex items-start gap-1.5 px-2 py-2">
				<button
					type="button"
					aria-label={open ? "收起" : "展开"}
					disabled={!canOpen}
					onClick={onToggle}
					className="mt-0.5 shrink-0 rounded-md p-0.5 text-text-muted hover:bg-surface-subtle disabled:opacity-30"
					data-testid="plan-trace-toggle"
				>
					{open && canOpen ? (
						<ChevronDown className="h-3.5 w-3.5" />
					) : (
						<ChevronRight className="h-3.5 w-3.5" />
					)}
				</button>
				<button
					type="button"
					disabled={!clickable}
					onClick={() => {
						onSelect({ kind: "group", key: g.key });
						if (canOpen && !open) onToggle();
					}}
					className={cn(
						"min-w-0 flex-1 text-left",
						greyed ? "text-text-muted" : "text-text-primary",
					)}
				>
					<div className="flex flex-wrap items-center gap-1.5 text-sm">
						<span className="font-medium">{g.kind === "item" ? "事项" : g.title}</span>
						<span
							className={cn("min-w-0", greyed ? "" : "text-text-secondary")}
							data-testid="plan-trace-summary"
						>
							{g.summary}
						</span>
						{g.activatedThisTurn ? <Badge tone="info">本轮激活</Badge> : null}
						{g.status && !(g.kind === "item" && g.item && !g.item.active) ? (
							<Badge tone={g.status.tone}>{g.status.label}</Badge>
						) : null}
					</div>
					{g.note ? <div className="mt-0.5 text-xs text-text-muted">{g.note}</div> : null}
				</button>
			</div>
			{open && canOpen ? (
				<div className="space-y-1 border-t border-surface-border px-2 py-2">
					{g.rows.map((r, i) => (
						<StepRow
							key={r.id}
							row={r}
							seq={i + 1}
							selected={selected?.kind === "row" && selected.id === r.id}
							onSelect={() => onSelect({ kind: "row", id: r.id, groupKey: g.key })}
						/>
					))}
				</div>
			) : null}
		</div>
	);
}

/** 组内一行：「层名 · 一句摘要 · 状态」＋ 耗时 */
function StepRow({
	row,
	seq,
	selected,
	onSelect,
}: {
	row: TraceRow;
	seq: number;
	selected: boolean;
	onSelect: () => void;
}) {
	const hit = row.kind === "guard" && row.status && row.status.tone !== "muted";
	return (
		<button
			type="button"
			onClick={onSelect}
			className={cn(
				"flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors duration-150",
				selected ? "bg-brand-accent/10" : "hover:bg-surface-subtle",
				hit ? "border-l-2 border-brand-warning" : "",
			)}
			data-testid="plan-trace-row"
			data-kind={row.kind}
		>
			<span className="w-4 shrink-0 font-mono text-text-muted">{seq}</span>
			<span className="shrink-0 font-medium text-text-primary">{row.name}</span>
			<span className="min-w-0 flex-1 truncate text-text-secondary" title={row.text}>
				{row.text}
			</span>
			{row.status ? <Badge tone={row.status.tone}>{row.status.label}</Badge> : null}
			<span className="shrink-0 font-mono text-text-muted">{row.ms ? fmtMs(row.ms) : ""}</span>
		</button>
	);
}

function money(v: number | null): string {
	if (v === null || !Number.isFinite(v)) return "—";
	return `$${v < 0.01 ? v.toFixed(4) : v.toFixed(3)}`;
}

/** 底部一行：本轮耗时 · token · 花费；按层拆 */
function CostFooter({ cost }: { cost: CostLine | null }) {
	return (
		<div
			className="space-y-0.5 border-t border-surface-border bg-surface-card px-3 py-2 text-xs text-text-secondary"
			data-testid="plan-trace-cost"
		>
			{cost ? (
				<>
					<div className="text-text-primary">
						本轮 {fmtMs(cost.totalMs)} · token{" "}
						{cost.tokens === null ? "—" : cost.tokens.toLocaleString("en-US")} · {money(cost.cost)}
					</div>
					<div className="text-text-muted">
						{cost.parts.length
							? cost.parts
									.map(
										(p) =>
											`${p.label} ${fmtMs(p.ms)}${p.tokens ? `（${p.tokens.toLocaleString("en-US")} tok）` : ""}`,
									)
									.join(" ｜ ")
							: "按层：—"}
					</div>
				</>
			) : (
				<div>本轮 — · token — · —</div>
			)}
		</div>
	);
}
